/**
 * Connect socket listeners for chat events.
 *
 * Two scopes of subscriptions:
 *  - `user:{myId}` room: joined automatically by the regular sockets/subscribe
 *    endpoint. We only attach listeners here for `message-created` so that
 *    unread bumps on conversations the user is NOT currently viewing arrive.
 *  - `conversation:{id}` room: joined explicitly for every conversation the
 *    user is actively viewing — the main open thread AND every open mini-chat.
 *    Without this, `message-edited`, `message-deleted`,
 *    `message-reaction-updated`, and `conversation-read` (which are broadcast
 *    only to the conversation room) never reach mini-chats.
 *    Listeners are attached globally — they fire only for sockets that have
 *    joined the relevant room.
 *
 * On reconnect we re-subscribe to every currently desired room and refetch
 * the conversations list to pick up anything missed.
 */
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDispatch, RootState } from "configureStore";
import { socket, socketGet } from "services/socket-client";
import {
  receiveMessage,
  bumpUnread,
  applyMessageEdited,
  applyMessageDeleted,
  applyReactionUpdate,
  fetchConversationsThunk,
  fetchMessagesThunk,
  fetchMiniChatConversationMetaThunk,
  bumpCrossCompanyUnread,
  updateConversationRead,
  openMiniChat,
} from "./chat.slice";
import { BroadcastMessageReaction, MessageReaction } from "types/Chat";
import { showBrowserNotification } from "./browser-notifications";
import { useAppSelector } from "hooks";

interface MessagePayload {
  conversationId: string;
  companyId?: string | null;
  conversationType?: "company" | "dm" | null;
  message: {
    id: string;
    conversationId: string;
    sender: string;
    content: string;
    attachment: {
      id: string;
      name: string;
      companyId: string;
      mimeType?: string;
      size?: number;
    } | null;
    createdAt: number;
    editedAt: number | null;
    deletedAt: number | null;
    reactions: MessageReaction[];
  };
}

interface EditedPayload {
  conversationId: string;
  messageId: string;
  content: string;
  editedAt: number;
}

interface DeletedPayload {
  conversationId: string;
  messageId: string;
  deletedAt?: number;
}

interface ReadPayload {
  conversationId: string;
  userId: string;
  lastReadAt: number;
}

interface ReactionUpdatedPayload {
  conversationId: string;
  messageId: string;
  reactions: BroadcastMessageReaction[];
}

export function useChatSocket(companyId: string | undefined) {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = useAppSelector((state) => state.authReducer.session);
  const myUserId = session?.id;
  const openConversationId = useSelector(
    (state: RootState) => state.chatReducer.openConversationId
  );
  const openConversationCompanyId = useSelector(
    (state: RootState) => state.chatReducer.openConversationCompanyId
  );
  const scopedOpenConversationId =
    openConversationCompanyId === companyId ? openConversationId : null;
  // Companies the user belongs to — used to look up names for cross-company toasts.
  const companies = useSelector(
    (state: RootState) => state.companySelectReducer?.companiesCollectionRaw || []
  ) as Array<{ id: string; name: string }>;
  const companyById = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    companyById.current = new Map(companies.map((c) => [c.id, c.name]));
  }, [companies]);
  // Conversation IDs the socket has been told to join. We track these so a
  // re-render of the subscription effect doesn't redundantly hit the server
  // for rooms we already joined. On reconnect we clear this and re-subscribe
  // everything. Stored as a plain object map (not Set) to keep the file
  // compatible with the project's TS target which doesn't enable Set
  // iteration / `--downlevelIteration`.
  const subscribedRoomsRef = useRef<Record<string, true>>({});
  // Mirror of the currently desired room ids so the reconnect handler (which
  // lives inside a useEffect with stale closure) can read fresh values.
  const desiredRoomsRef = useRef<string[]>([]);
  const knownConversationsRef = useRef<Set<string>>(new Set());
  // Active mini-chats — every open mini-chat needs its conversation room
  // joined so real-time edit / delete / reaction events flow into it.
  const activeMiniChats = useSelector(
    (state: RootState) => state.chatReducer.activeMiniChats
  );
  // Each mini-chat may belong to a different company — subscribe using its own companyId.
  const conversationSubscriptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const chat of activeMiniChats) {
      map.set(String(chat.conversationId), String(chat.companyId));
    }
    if (scopedOpenConversationId && companyId) {
      map.set(String(scopedOpenConversationId), String(companyId));
    }
    return map;
  }, [activeMiniChats, scopedOpenConversationId, companyId]);
  // Dedupe: server may broadcast `message-created` to both `conversation:{id}`
  // and `user:{userId}` rooms; a socket joined to both gets the event twice.
  // Track recently-seen message ids so reducers (esp. bumpUnread) only fire once.
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const seenOrderRef = useRef<string[]>([]);
  const SEEN_LIMIT = 256;
  const conversations = useSelector(
    (state: RootState) => state.chatReducer.conversations
  );

  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
    knownConversationsRef.current = new Set(conversations.map((c) => c.id));
  }, [conversations]);

  const activeMiniChatsRef = useRef(activeMiniChats);
  useEffect(() => {
    activeMiniChatsRef.current = activeMiniChats;
  }, [activeMiniChats]);

  // Subscribe to every conversation room the user is actively viewing —
  // the main open thread AND every active mini-chat. Joining a room on the
  // server is idempotent, so re-running the effect for an already-subscribed
  // id is a no-op (we still guard with subscribedRoomsRef to avoid the HTTP
  // round-trip). Stale subscriptions when a mini-chat closes are harmless:
  // the matching reducers no-op for threads that aren't loaded.
  useEffect(() => {
    if (!companyId && conversationSubscriptions.size === 0) {
      subscribedRoomsRef.current = {};
      desiredRoomsRef.current = [];
      return;
    }

    const desired: Array<{ conversationId: string; companyId: string }> = [];
    conversationSubscriptions.forEach((chatCompanyId, conversationId) => {
      desired.push({ conversationId, companyId: chatCompanyId });
    });
    desiredRoomsRef.current = desired.map((d) => d.conversationId);

    desired.forEach(({ conversationId, companyId: chatCompanyId }) => {
      const roomKey = `${chatCompanyId}:${conversationId}`;
      if (subscribedRoomsRef.current[roomKey]) return;
      subscribedRoomsRef.current[roomKey] = true;
      socketGet(
        `/${chatCompanyId}/sockets/subscribe-conversation/${conversationId}`
      ).catch((err: unknown) => {
        delete subscribedRoomsRef.current[roomKey];
        const status = (err as { statusCode?: number })?.statusCode;
        // eslint-disable-next-line no-console
        console.warn("[chat-socket] subscribe-conversation failed", {
          conversationId,
          companyId: chatCompanyId,
          statusCode: status,
        });
      });
    });
  }, [companyId, conversationSubscriptions]);

  useEffect(() => {
    if (!companyId) return;

    const onMessageCreated = (...args: unknown[]) => {
      const payload = args[0] as MessagePayload | undefined;
      if (!payload?.message) return;
      // Dedupe duplicate deliveries (conversation room + user room)
      if (seenMessageIdsRef.current.has(payload.message.id)) return;
      seenMessageIdsRef.current.add(payload.message.id);
      seenOrderRef.current.push(payload.message.id);
      if (seenOrderRef.current.length > SEEN_LIMIT) {
        const evicted = seenOrderRef.current.shift();
        if (evicted) seenMessageIdsRef.current.delete(evicted);
      }

      const isMine = String(payload.message.sender) === String(myUserId);

      // ── Cross-company branch ─────────────────────────────────────────────
      // The sender's user:{id} room is independent of the active company,
      // so the user may receive events for conversations in companies they
      // are NOT currently viewing. Bump that company's unread badge and
      // (for DMs only) surface a toast / OS notification.
      const eventCompanyId = payload.companyId || null;
      const isCrossCompany =
        !!eventCompanyId &&
        !!companyId &&
        String(eventCompanyId) !== String(companyId);
      if (isCrossCompany && isMine) {
        return;
      }
      if (isCrossCompany && !isMine) {
        const isMiniChatOpenAndActive = activeMiniChatsRef.current.some(
          c => String(c.conversationId) === String(payload.conversationId) && !c.isMinimized
        );
        const companyName = companyById.current.get(eventCompanyId!) || "";
        if (!isMiniChatOpenAndActive) {
          dispatch(bumpCrossCompanyUnread({ companyId: eventCompanyId!, companyName, conversationId: payload.conversationId }));
          dispatch(bumpUnread({ conversationId: payload.conversationId }));
        }

        let preview = payload.message.content.slice(0, 80);
        if (!preview && payload.message.attachment) {
          preview = payload.message.attachment.name;
        }

        const convoType = payload.conversationType || "dm";

        // Store message + load history using the message's company context.
        dispatch(
          receiveMessage({
            ...payload.message,
            reactions: payload.message.reactions || [],
          })
        );
        dispatch(
          fetchMessagesThunk({
            companyId: eventCompanyId!,
            conversationId: payload.conversationId,
            limit: 100,
          })
        );
        if (!isMiniChatOpenAndActive) {
          dispatch(
            fetchMiniChatConversationMetaThunk({
              companyId: eventCompanyId!,
              conversationId: payload.conversationId,
            })
          );
        }

        dispatch(
          openMiniChat({
            conversationId: payload.conversationId,
            companyId: eventCompanyId!,
            conversationType: convoType,
            forceExpand: true,
          })
        );

        if (!isMiniChatOpenAndActive) {
          toast(
            t("new_dm_in_other_company", {
              company: companyName || t("link_kind_link"),
              defaultValue: `New message in ${companyName}`,
            }),
            {
              description: preview,
            }
          );

          showBrowserNotification({
            title: companyName
              ? `${t("new_message")} — ${companyName}`
              : t("new_message"),
            body: preview,
            onClick: () => window.focus(),
          });
        }
        return;
      }

      // ── Same-company branch ─────────────────────────────────────────────
      dispatch(
        receiveMessage({
          ...payload.message,
          reactions: payload.message.reactions || [],
        })
      );
      const isMiniChatOpenAndActive = activeMiniChatsRef.current.some(
        c => String(c.conversationId) === String(payload.conversationId) && !c.isMinimized
      );

      if (
        !isMine &&
        payload.conversationId !== scopedOpenConversationId &&
        !isMiniChatOpenAndActive
      ) {
        dispatch(bumpUnread({ conversationId: payload.conversationId }));
        
        const convo = conversationsRef.current.find(c => c.id === payload.conversationId);
        dispatch(openMiniChat({
          conversationId: payload.conversationId,
          companyId: companyId!,
          title: convo?.type === "company" ? t("company_chat") : convo?.otherParticipant?.name,
          avatarFileId: convo?.otherParticipant?.avatarFileId,
          conversationType: convo?.type,
          forceExpand: true // Auto-expand so user sees the window immediately
        }));
      }
      // For brand-new DMs the recipient hasn't seen — fetch the list so the new
      // conversation appears with correct preview/avatar. Skip refetch if we
      // already know the conversation (avoids broadcast-storm refetches).
      if (!knownConversationsRef.current.has(payload.conversationId)) {
        dispatch(fetchConversationsThunk(companyId));
      }
    };

    const onMessageEdited = (...args: unknown[]) => {
      const payload = args[0] as EditedPayload | undefined;
      if (!payload) return;
      dispatch(
        applyMessageEdited({
          conversationId: payload.conversationId,
          messageId: payload.messageId,
          content: payload.content,
          editedAt: payload.editedAt,
        })
      );
    };

    const onMessageDeleted = (...args: unknown[]) => {
      const payload = args[0] as DeletedPayload | undefined;
      if (!payload) return;
      dispatch(applyMessageDeleted(payload));
    };

    const onConversationRead = (...args: unknown[]) => {
      const payload = args[0] as ReadPayload | undefined;
      if (!payload || !myUserId) return;
      // We no longer carry a per-user participants list client-side: the slice
      // updates `maxOtherLastReadAt` (used by the status icon) and, for DMs,
      // `otherParticipant.lastReadAt`. Names for the company-chat "Viewed by"
      // popover are loaded lazily on hover via GET .../readers — no refetch
      // needed here.
      dispatch(updateConversationRead({ ...payload, currentUserId: myUserId }));
    };

    const onReactionUpdated = (...args: unknown[]) => {
      const payload = args[0] as ReactionUpdatedPayload | undefined;
      if (!payload) return;
      // Server-provided array is often generic — we tell the reducer to
      // preserve our local "me" flag if it doesn't find one in the payload.
      dispatch(applyReactionUpdate({
        ...payload,
        fromSocket: true,
      }));
    };

    const onReconnect = () => {
      dispatch(fetchConversationsThunk(companyId));
      // Forget which rooms we'd previously joined and re-subscribe everything
      // we currently want — main thread + all active mini-chats.
      subscribedRoomsRef.current = {};
      conversationSubscriptions.forEach((chatCompanyId, conversationId) => {
        const roomKey = `${chatCompanyId}:${conversationId}`;
        subscribedRoomsRef.current[roomKey] = true;
        socketGet(
          `/${chatCompanyId}/sockets/subscribe-conversation/${conversationId}`
        ).catch(() => {
          delete subscribedRoomsRef.current[roomKey];
        });
      });
    };

    socket.on("message-created", onMessageCreated as any);
    socket.on("message-edited", onMessageEdited as any);
    socket.on("message-deleted", onMessageDeleted as any);
    socket.on("conversation-read", onConversationRead as any);
    socket.on("message-reaction-updated", onReactionUpdated as any);
    socket.on("reconnect", onReconnect as any);

    return () => {
      socket.off("message-created", onMessageCreated as any);
      socket.off("message-edited", onMessageEdited as any);
      socket.off("message-deleted", onMessageDeleted as any);
      socket.off("conversation-read", onConversationRead as any);
      socket.off("message-reaction-updated", onReactionUpdated as any);
      socket.off("reconnect", onReconnect as any);
    };
  }, [companyId, dispatch, myUserId, scopedOpenConversationId, navigate, t, conversationSubscriptions]);
}
